import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { setCanonical, setMeta } from '@/utils/seo';
import { FAQSchema, BreadcrumbSchema, SpeakableSchema, ServiceSchema } from '@/components/SEOSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_FAQS = (name) => [
  { question: `How can OFWs in ${name} manage their Philippine assets?`, answer: `OFWs in ${name} can use LegaSecure's Online SPA to authorize a family member in the Philippines to handle property transactions, bank accounts, and legal matters without needing to fly home.` },
  { question: `How do I create an SPA from ${name}?`, answer: `With LegaSecure, you can create an Apostille-ready SPA online from ${name}. Traditional SPAs require visiting a Philippine Consulate, but LegaSecure's digital platform streamlines the process.` },
  { question: `What happens to my Philippine property if something happens to me in ${name}?`, answer: `Without proper estate planning, your family may face legal complications accessing your assets. LegaSecure's Guardian Mandate and Digital Vault ensure your family has clear instructions and document access in emergencies.` },
  { question: `Is LegaSecure available for Filipinos in ${name}?`, answer: `Yes! LegaSecure serves Filipinos worldwide, including those in ${name}. All services — Online SPA, Guardian Mandate, and Digital Vault — are accessible online from anywhere.` },
];

export default function CountryPage() {
  const { slug } = useParams();
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/countries/${slug}`).then(r => {
      setCountry(r.data);
      setCanonical(`/ofw/${slug}`);
      setMeta(`Estate Planning for OFW in ${r.data.name} | LegaSecure Guide`, `Estate planning guide for Filipinos and OFWs in ${r.data.name}. Online SPA, guardian mandates, and digital vault access from ${r.data.name}.`);
    }).catch(() => setCountry(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="ls-container py-20 text-center"><i className="fa-solid fa-spinner fa-spin text-2xl text-[var(--ls-navy)]"></i></div>;
  if (!country) return <div className="ls-container py-20 text-center"><h1 className="text-2xl font-bold">Country not found</h1><Link to="/all-countries" className="ls-btn-gold mt-4 inline-block">Browse All Countries</Link></div>;

  const faqs = COUNTRY_FAQS(country.name);

  return (
    <div data-testid="country-page">
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'OFW Countries', url: '/all-countries' }, { name: country.name }]} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />
      <ServiceSchema />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/80"></div>
        </div>
        <div className="ls-container relative z-10 py-12">
          <nav className="text-sm text-[var(--ls-text-light)] mb-4">
            <Link to="/" className="hover:text-[var(--ls-gold)]">Home</Link> / <Link to="/all-countries" className="hover:text-[var(--ls-gold)]">OFW Countries</Link> / <span className="text-[var(--ls-navy)] font-medium">{country.name}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold font-['Crimson_Pro'] mb-3" style={{ color: '#1B365D' }}>Estate Planning for OFW in {country.name}</h1>
          <p className="speakable-intro text-lg max-w-2xl" style={{ color: '#4A5568' }}>
            You're working hard in {country.name} to build a better life for your family back home. Don't let distance prevent you from protecting what matters most. LegaSecure helps {country.ofw_estimate} Filipino workers in {country.name} manage their Philippine affairs remotely.
          </p>
          <div className="flex flex-wrap gap-4 mt-6">
            <a href="https://legasecure.com" className="ls-btn-navy">Get Started Free</a>
            <Link to={`/spa/${slug}`} className="ls-btn-outline">
              SPA from {country.name} <i className="fa-solid fa-arrow-right ml-1"></i>
            </Link>
          </div>
        </div>
      </section>

      <section className="ls-section">
        <div className="ls-container">
          <div className="ls-card mb-8 bg-amber-50 border border-amber-200">
            <p className="text-sm text-amber-800">
              <i className="fa-solid fa-triangle-exclamation mr-2"></i>
              <strong>Real Scenario:</strong> You're working in {country.name}. You get hospitalized or face an emergency. Your family in the Philippines can't access your bank account to pay bills or manage your property. <strong>What now?</strong> This is exactly what LegaSecure prevents.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: 'fa-file-signature', title: `Online SPA from ${country.name}`, desc: `Create a Special Power of Attorney from ${country.name} without visiting a consulate. Authorize your family to sell property, manage banks, or sign contracts.`, link: `/spa/${slug}` },
              { icon: 'fa-user-shield', title: 'Guardian Mandate', desc: `If you're in ${country.name} and can't care for your children, your Guardian gets instant access to care instructions, medical contacts, and school records.` },
              { icon: 'fa-vault', title: 'Digital Vault', desc: `Store your Philippine property titles, bank details, and insurance policies in an encrypted vault accessible from ${country.name} or anywhere else.` },
            ].map((s, i) => (
              <div key={i} className="ls-card">
                <i className={`fa-solid ${s.icon} text-[var(--ls-gold)] text-2xl mb-3`}></i>
                <h3 className="font-bold mb-2">{s.title}</h3>
                <p className="text-sm text-[var(--ls-text-light)] mb-3">{s.desc}</p>
                {s.link ? (
                  <Link to={s.link} className="text-[var(--ls-gold)] text-sm font-medium">Learn more <i className="fa-solid fa-arrow-right ml-1"></i></Link>
                ) : (
                  <a href="https://legasecure.com" className="text-[var(--ls-gold)] text-sm font-medium">Learn more <i className="fa-solid fa-arrow-right ml-1"></i></a>
                )}
              </div>
            ))}
          </div>

          <div className="ls-card prose-content mb-12">
            <h2>Why OFWs in {country.name} Need Estate Planning</h2>
            <p>There are an estimated {country.ofw_estimate} Filipino workers in {country.name}, contributing significantly to the Philippine economy through remittances. But working abroad comes with unique challenges when it comes to managing your assets and protecting your family back home.</p>
            <h3>Common Challenges for OFWs in {country.name}</h3>
            <ul>
              <li><strong>Property Management:</strong> Selling, buying, or managing real property in the Philippines requires physical presence — unless you have an SPA.</li>
              <li><strong>Bank Account Access:</strong> If you're incapacitated, your family may not be able to access your accounts to pay for your children's needs.</li>
              <li><strong>Emergency Situations:</strong> Without clear instructions, your family faces chaos during emergencies.</li>
              <li><strong>Legal Complexity:</strong> Navigating Philippine law from {country.name} adds layers of complexity and cost.</li>
            </ul>
            <h3>How LegaSecure Solves This</h3>
            <p>LegaSecure gives you control from {country.name}. Create Online SPAs, designate guardians, and store critical documents — all from your phone or computer. No flights home. No expensive lawyer visits. Just peace of mind.</p>
          </div>

          {/* How It Works - Mini */}
          <div className="ls-card mb-12 bg-[var(--ls-bg)]">
            <h2 className="text-xl font-bold mb-6 text-center">Getting Started Is Easy</h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="w-10 h-10 rounded-full bg-[var(--ls-gold)] text-white font-bold flex items-center justify-center mx-auto mb-2">1</div>
                <p className="text-sm font-medium text-[var(--ls-navy)]">Sign Up Free</p>
                <p className="text-xs text-[var(--ls-text-light)]">Takes less than 5 minutes</p>
              </div>
              <div>
                <div className="w-10 h-10 rounded-full bg-[var(--ls-gold)] text-white font-bold flex items-center justify-center mx-auto mb-2">2</div>
                <p className="text-sm font-medium text-[var(--ls-navy)]">Set Up Your Plan</p>
                <p className="text-xs text-[var(--ls-text-light)]">SPA, Guardian, or Vault</p>
              </div>
              <div>
                <div className="w-10 h-10 rounded-full bg-[var(--ls-gold)] text-white font-bold flex items-center justify-center mx-auto mb-2">3</div>
                <p className="text-sm font-medium text-[var(--ls-navy)]">Manage from {country.name}</p>
                <p className="text-xs text-[var(--ls-text-light)]">No flights home needed</p>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((f, i) => <FAQItem key={i} question={f.question} answer={f.answer} />)}
            </div>
          </div>

          {country.related_countries?.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Other OFW Destinations in {country.region}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {country.related_countries.map(c => (
                  <Link key={c.slug} to={`/ofw/${c.slug}`} className="ls-card p-4 flex items-center gap-3">
                    <i className="fa-solid fa-globe text-[var(--ls-gold)]"></i>
                    <div>
                      <p className="font-semibold text-sm">{c.name}</p>
                      <p className="text-xs text-[var(--ls-text-light)]">{c.ofw_estimate} OFWs</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Popular Philippine Cities</h2>
            <p className="text-sm text-[var(--ls-text-light)] mb-4">Estate planning guides for major Philippine cities where OFWs from {country.name} have family and property.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: 'Manila', slug: 'manila' },
                { name: 'Quezon City', slug: 'quezon-city' },
                { name: 'Cebu City', slug: 'cebu-city' },
                { name: 'Davao City', slug: 'davao-city' },
                { name: 'Makati', slug: 'makati' },
                { name: 'Iloilo City', slug: 'iloilo-city' },
                { name: 'Cagayan de Oro', slug: 'cagayan-de-oro' },
                { name: 'Bacolod', slug: 'bacolod' },
              ].map(c => (
                <Link key={c.slug} to={`/city/${c.slug}`} className="ls-card p-3 flex items-center gap-2 text-sm hover:border-[var(--ls-gold)] border border-transparent">
                  <i className="fa-solid fa-city text-[var(--ls-gold)]"></i>
                  <span className="text-[var(--ls-navy)] font-medium">{c.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[var(--ls-navy)] py-12">
        <div className="ls-container text-center">
          <h2 className="text-2xl font-bold text-white mb-3 font-['Crimson_Pro']">Your family back home is counting on you.</h2>
          <p className="text-white/70 mb-6 max-w-lg mx-auto">You're in {country.name} working hard for them. Make sure they're protected no matter what happens. It takes less than 5 minutes to start.</p>
          <a href="https://legasecure.com" className="ls-btn-gold text-lg px-8 py-3">Start Your Free Plan <i className="fa-solid fa-arrow-right ml-2"></i></a>
        </div>
      </section>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ls-card cursor-pointer" onClick={() => setOpen(!open)}>
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-sm text-[var(--ls-navy)] pr-4">{question}</h3>
        <i className={`fa-solid fa-chevron-down text-[var(--ls-text-light)] transition-transform text-xs ${open ? 'rotate-180' : ''}`}></i>
      </div>
      {open && <p className="mt-2 text-sm text-[var(--ls-text-light)] leading-relaxed">{answer}</p>}
    </div>
  );
}
